import { apiClient } from "../services/apiClient";
import { ATP_API_URL } from "../config/apiConfig";

const isValidPath = (p) => p && p !== "string" && p !== "";

const toDocEntry = (key, label, path) => ({
  key,
  documentName: label,
  files: [
    {
      isUploaded: true,
      fileName: path.split(/[\\/]/).pop() || label,
      filePath: path,
    },
  ],
});

/**
 * Builds a normalised documents array from nested sub-objects.
 * Only includes a doc when the API flag (isAadharCard, isPANCard etc.) is true
 * AND a valid file path exists.
 */
const buildDocuments = (r) => {
  const prop = r.proprietorshipDetails || {};
  const partner = r.partnershipDetails || {};
  const llp = r.llpDetails || {};
  const pvt = r.privatePublicLimitedCompanyDetails || {};
  const trust = r.trustDetails || {};
  const msme = r.msmeDetails || {};
  const utility = r.utilityBillDetails || {};
  const bank = r.bankDetails || {};
  const pan = r.panDetails || {};
  const aadhar = r.adharDetails || {};

  // [key, label, path, uploadedFlag]
  const candidates = [
    [
      "doc_aadhar",
      "Aadhar Card",
      prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        r.aadharCardPath,
      prop.isAadharCard ||
        aadhar?.isAadharCard ||
        partner.isAadharOfBothPartners ||
        llp.isAadharOfBothPartners,
    ],
    [
      "aadharCardBackPath",
      "Aadhar Card (Back)",
      r.aadharCardBackPath ||
        prop.aadharCardBackPath ||
        aadhar.aadharCardBackPath ||
        llp.aadharCardBackPath ||
        partner.aadharCardBackPath,
      true,
    ],
    [
      "doc_pan",
      "PAN Card",
      prop.panCardPath || pan.panCardPath,
      prop.isPANCard || pan?.isPANCard,
    ],
    [
      "doc_partnershipDeed",
      "Partnership Deed",
      partner.partnershipDeedPath || llp.partnershipDeedPath,
      partner.isPartnershipDeed || llp.isPartnershipDeed,
    ],
    [
      "doc_aadharBoth",
      "Aadhar of Both Partners",
      partner.aadharOfBothPartnersPath ||
        llp.aadharOfBothPartnersPath ||
        prop.aadharCardPath ||
        aadhar.aadharCardPath ||
        r.aadharCardPath,
      partner.isAadharOfBothPartners ||
        llp.isAadharOfBothPartners ||
        prop.isAadharCard ||
        aadhar?.isAadharCard,
    ],
    [
      "doc_aadharBothSecondary",
      "Aadhar of Secondary Partner",
      partner.aadharOfBothPartnersPathSecond ||
        llp.aadharOfBothPartnersPathSecond,
      partner.isAadharOfBothPartnersSecond || llp.isAadharOfBothPartnersSecond,
    ],
    [
      "doc_llpCertificate",
      "LLP Certificate",
      llp.uploadLLPCertificatePath,
      llp.isLLPCertificate,
    ],
    [
      "doc_coi",
      "Certificate of Incorporation",
      pvt.certificateOfIncorporationPath,
      pvt.isCertificateOfIncorporation,
    ],
    ["doc_directorPan", "Director PAN", pvt.directorPANPath, pvt.isDirectorPan],
    [
      "doc_directorAadhar",
      "Director Aadhar",
      pvt.directorAadharPath,
      pvt.isDirectorAadhar,
    ],
    ["doc_trustDeed", "Trust Deed", trust.trustDeedPath, trust.isTrustDeed],
    ["doc_panTrust", "PAN of Trust", trust.trustPanPath, trust.isPANOfTrust],
    [
      "doc_aadharTrustee",
      "Aadhar of Trustee",
      trust.trusteeAadharPath,
      trust.isAadharOfTrustee,
    ],
    [
      "doc_panTrustee",
      "PAN of Trustee",
      trust.trusteePanPath,
      trust.isPANOfTrustee,
    ],
    [
      "doc_msme_certificate",
      "MSME Certificate",
      msme.msmeCertificatePath,
      msme.isMSMECertificate,
    ],
    [
      "doc_utility_bill",
      "Utility Bill",
      utility.utilityBillDocumentPath,
      utility.isUtilityBillDocument,
    ],
    [
      "doc_bank_proof",
      "Passbook / Cancelled Cheque",
      bank.passbookOrCancelledChequePath,
      bank.isPassbookOrCancelledCheque,
    ],
    [
      "third_party_file",
      "Signed Form (Third Party)",
      bank.uploadSignedFormPath,
      bank.isSignedForm,
    ],
  ];

  const documents = candidates
    .filter(([, , path]) => isValidPath(path))
    .map(([key, label, path]) => toDocEntry(key, label, path));

  // GST certificates — use gstCertificatePath directly (no separate flag)
  (r.gstList || []).forEach((g, i) => {
    const certPath = isValidPath(g.gstCertificatePath)
      ? g.gstCertificatePath
      : null;
    documents.push({
      key: `gst_file_${i}`,
      documentName: `GST Certificate — ${g.gstNumber || `#${i + 1}`}`,
      files: certPath
        ? [
            {
              isUploaded: true,
              fileName: certPath.split(/[\\/]/).pop() || "GST",
              filePath: certPath,
            },
          ]
        : [],
    });
  });

  return documents;
};

/**
 * Fetches existing KYC/entity data by onboardingId (entityPrimaryId).
 */
export const fetchKYCDetailsByIdAction = async (id) => {
  try {
    const body = {
      id,
      category: null,
      searchValue: null,
      requireKYC: null,
      documentStatus: null,
      isVerified: null,
      isRegisterdUnderMSME: null,
      entityLOBType: null,
      startDate: null,
      endDate: null,
      pageNumber: 1,
      pageSize: 1,
    };

    const response = await apiClient.post(
      ATP_API_URL.GET_ALL_DATA_SEARCH,
      body,
    );

    if (response?.status === 200 && response.data) {
      const raw = response.data;
      const record = Array.isArray(raw)
        ? raw[0]
        : Array.isArray(raw?.data)
          ? raw.data[0]
          : raw?.data || null;

      if (!record) return { success: false, data: null, documents: [] };

      return { success: true, data: record, documents: buildDocuments(record) };
    }

    return { success: false, data: null, documents: [] };
  } catch (error) {
    return { success: false, data: null, documents: [], error: error.message };
  }
};
